import { shallowRef, triggerRef, unref } from "vue";

/**
 * @typedef {import("#ce/components/SpFinder/engine/finder-filter-protocol").FinderFilterState} FinderFilterState
 * @typedef {import("#ce/components/SpFinder/engine/finder-filter-protocol").FinderFilterActions} FinderFilterActions
 * @typedef {import("#ce/components/SpFinder/engine/finder-filter-protocol").FinderFilterConnectPayload} FinderFilterConnectPayload
 */

/**
 * @typedef {object} FinderFilterRegistration
 * @property {HTMLElement} element - the filter's host, also the registry key.
 * @property {number} id - stable for as long as the filter stays registered, so a list keyed by it
 *   never reuses a row across two filters. `documentIndex` shifts as filters come and go.
 * @property {string} name
 * @property {string} label
 * @property {string | undefined} icon
 * @property {FinderFilterActions} actions
 * @property {FinderFilterState} state - the last snapshot.
 * @property {number} documentIndex - position among the slot's assigned elements; -1 until the
 *   slot has been reconciled.
 * @property {HTMLElement | undefined} assignedElement - the element the slot assigns, which is the
 *   host itself unless a wrapper carries it; undefined until the slot has been reconciled. The row
 *   lays this out, so it is what the bar ranks.
 * @property {number | undefined} activatedAt - monotonic stamp from when `active` last flipped
 *   on; undefined while inactive.
 */

/** The state a registration is measured against before its first snapshot. */
const INITIAL_STATE = Object.freeze({ active: false, open: false, withheld: false, clearable: true });

/**
 * The bar's registry of announced filters, keyed by host element so two filters may share a
 * `name`. The Map lives in a `shallowRef` and is triggered after each mutation.
 *
 * @param {import("vue").Ref<HTMLSlotElement | null | undefined>} slot - the bar's default slot;
 *   its assigned elements decide which registrations still belong to the bar.
 */
export function useFinderFilterRegistry(slot) {
  /** @type {import("vue").ShallowRef<Map<HTMLElement, FinderFilterRegistration>>} */
  const registry = shallowRef(new Map());
  let activationCounter = 0;
  let idCounter = 0;

  /**
   * Registers or re-registers a filter and applies its announced state as a transition from the
   * state the bar last knew for it.
   *
   * @param {HTMLElement} element
   * @param {FinderFilterConnectPayload} payload
   */
  function register(element, payload) {
    const previous = registry.value.get(element);
    const registration = {
      element,
      id: previous?.id ?? (idCounter += 1),
      name: payload.name,
      label: payload.label,
      icon: payload.icon,
      actions: payload.actions,
      state: previous?.state ?? INITIAL_STATE,
      documentIndex: previous?.documentIndex ?? -1,
      assignedElement: previous?.assignedElement,
      activatedAt: previous?.activatedAt,
    };

    registry.value.set(element, registration);
    transition(registration, payload.state);
    prune();
  }

  /**
   * Applies a filter's snapshot. A snapshot from an element that has not announced is ignored.
   *
   * @param {HTMLElement} element
   * @param {FinderFilterState} next
   */
  function applyState(element, next) {
    const registration = registry.value.get(element);

    if (!registration) {
      return;
    }

    transition(registration, next);
    triggerRef(registry);
  }

  /**
   * @param {FinderFilterRegistration} registration
   * @param {FinderFilterState} next
   */
  function transition(registration, next) {
    const previous = registration.state;
    registration.state = next;

    if (next.active && !previous.active) {
      activationCounter += 1;
      registration.activatedAt = activationCounter;
    } else if (!next.active) {
      registration.activatedAt = undefined;
    }

    if (next.open && !previous.open) {
      // `slotchange` and the mutation observer prune asynchronously, so membership is reconciled here first.
      prune();

      if (registry.value.has(registration.element)) {
        closeOthers(registration);
      }
    }
  }

  /**
   * Mutual exclusion: every other open registration is told to close. A withheld registration
   * is left alone.
   *
   * @param {FinderFilterRegistration} opened
   */
  function closeOthers(opened) {
    for (const registration of registry.value.values()) {
      if (registration !== opened && registration.state.open && !registration.state.withheld) {
        registration.actions.close();
      }
    }
  }

  /**
   * Drops every registration whose element the slot no longer assigns, directly or inside a
   * slotted wrapper, and refreshes the remaining document indices and assigned elements. Does
   * nothing until the slot has rendered.
   */
  function prune() {
    const slotElement = unref(slot);

    if (!slotElement) {
      return;
    }

    const assigned = slotElement.assignedElements({ flatten: true });

    for (const [element, registration] of registry.value) {
      const index = assigned.findIndex((candidate) => candidate === element || candidate.contains(element));

      if (index === -1) {
        registry.value.delete(element);
      } else {
        registration.documentIndex = index;
        registration.assignedElement = assigned[index];
      }
    }

    triggerRef(registry);
  }

  function clear() {
    registry.value = new Map();
  }

  return { registry, register, applyState, prune, clear };
}
